import { z } from 'zod';
/**
 * Die Stufen, von der dringlichsten zur mildesten.
 *
 * Die Reihenfolge ist die Auswertungsreihenfolge: Der erste Treffer gilt. Weil die
 * beiden Richtungen sich nicht überschneiden — unter 50 % gegen über 115 % —,
 * genügt innerhalb jeder Richtung die absteigende Strenge. Die Verzahnung der
 * beiden Richtungen bestimmt zusätzlich, was in der E-Mail obenan steht.
 */
export const ALERT_LEVELS = [
    {
        key: 'eskalation',
        label: 'Eskalation',
        direction: 'annäherung',
        rank: 3,
        threshold: 15,
        meaning: 'Der Indikator steht praktisch auf dem Stop. Die Position wird gleich ausgelöst.',
    },
    {
        key: 'dringende-handlung',
        label: 'Dringende Handlung erforderlich',
        direction: 'entfernung',
        rank: 3,
        threshold: 160,
        meaning: 'Der Abstand hat sich mehr als eineinhalbfacht. Der Stop hängt weit zurück und gibt ' +
            'einen erheblichen Teil des Gewinns wieder her, falls es dreht.',
    },
    {
        key: 'dringende-warnung',
        label: 'Dringende Warnung',
        direction: 'annäherung',
        rank: 2,
        threshold: 30,
        meaning: 'Vom ursprünglichen Puffer ist weniger als ein Drittel übrig.',
    },
    {
        key: 'dringlicher-hinweis',
        label: 'Dringlicher Hinweis',
        direction: 'entfernung',
        rank: 2,
        threshold: 135,
        meaning: 'Der Abstand ist deutlich gewachsen. Ein Nachziehen des Stops sichert Gewinn.',
    },
    {
        key: 'warnung',
        label: 'Warnung',
        direction: 'annäherung',
        rank: 1,
        threshold: 50,
        meaning: 'Der Indikator hat die Hälfte des Weges zum Stop zurückgelegt.',
    },
    {
        key: 'hinweis',
        label: 'Hinweis',
        direction: 'entfernung',
        rank: 1,
        threshold: 115,
        meaning: 'Der Abstand wächst. Noch kein Handlungsdruck, aber im Blick zu behalten.',
    },
];
/**
 * Nachkommastellen des Anteils — zugleich die Genauigkeit der Entscheidung.
 *
 * **Gerundet wird genau einmal, und zwar vor dem Vergleich.** Der Grund ist nicht
 * Schönheit, sondern Fließkomma: `(11.5 / 10) * 100` ergibt `114.99999999999999`.
 * Ein Abstand, der rechnerisch exakt auf 115 % liegt, fiele damit unter die
 * Schwelle — die Nachricht bliebe aus, und in der Oberfläche stünde trotzdem
 * „115,0 %".
 *
 * Ein Widerspruch zwischen angezeigter Zahl und getroffener Entscheidung ist
 * genau das, was das Vertrauen in so eine Meldung zerstört. Deshalb entscheidet
 * dieselbe gerundete Zahl, die auch angezeigt wird: Beide können nicht mehr
 * auseinanderlaufen, weil es nur noch eine gibt.
 */
export const ALERT_PERCENT_DIGITS = 1;
const roundPercent = (value) => {
    const faktor = 10 ** ALERT_PERCENT_DIGITS;
    return Math.round(value * faktor) / faktor;
};
/** Trifft der Anteil diese Stufe? */
const matches = (level, percent) => level.direction === 'annäherung' ? percent <= level.threshold : percent >= level.threshold;
/**
 * Wertet einen Abstand gegen seine Bezugsgröße aus.
 *
 * `referenceDistance` ist der beim Verbinden festgehaltene Abstand,
 * `currentDistance` derselbe Abstand am jüngsten Handelstag. Beide sind
 * vorzeichenbehaftet: Indikator minus eingebuchter Stop.
 *
 * **Eine negative oder verschwindende Bezugsgröße wird nicht ausgewertet.** Lag
 * der Indikator beim Verbinden auf oder unter dem Stop, dreht die Division das
 * Vorzeichen um, und aus einer Verschlechterung würde ein steigender Anteil — die
 * Nachricht sagte dann das Gegenteil dessen, was geschieht. Lieber keine Aussage
 * als eine verkehrte.
 */
export const evaluateStopLossAlert = (referenceDistance, currentDistance) => {
    if (referenceDistance === null) {
        return { status: 'nicht-auswertbar', reason: 'Kein Bezugsabstand festgehalten' };
    }
    if (referenceDistance <= 0) {
        return {
            status: 'nicht-auswertbar',
            reason: 'Der Indikator lag beim Verbinden nicht über dem eingebuchten Stop',
        };
    }
    if (currentDistance === null) {
        return {
            status: 'nicht-auswertbar',
            reason: 'Der aktuelle Abstand lässt sich nicht bestimmen',
        };
    }
    const percent = roundPercent((currentDistance / referenceDistance) * 100);
    const level = ALERT_LEVELS.find((candidate) => matches(candidate, percent));
    return level ? { status: 'auffällig', percent, level } : { status: 'unauffällig', percent };
};
/**
 * Was das Nachziehen des Stop-Loss bewirkt hat.
 *
 * In `shared`, weil die Oberfläche die Antwort auswertet: Sie meldet den neuen
 * Kurs zurück und weist darauf hin, dass beim Broker noch die alte Order liegt.
 */
export const stopLossSyncResultSchema = z.object({
    previousPrice: z.number().finite(),
    newPrice: z.number().finite(),
    referenceDistance: z.number().finite(),
    indicatorPrice: z.number().finite(),
    tradingDay: z.string().nullable(),
    bookingOutdated: z.boolean(),
});
//# sourceMappingURL=stop-loss-alert.js.map